import type {
    Benefit,
    DifferenceCard,
    Faq,
    ProcessStep,
    TeamMember,
    Testimonial
} from '@/types'

export const heroFeatures = [
    {
        icon: '/images/phone-fast-1.svg',
        title: 'FREE ESTIMATES',
        subtitle: 'Call 774-301-1382 or send the form for a free, written estimate.'
    },
    {
        icon: '/images/ng/shield-check.svg',
        title: 'LICENSED & INSURED',
        subtitle: 'Fully licensed and insured for your protection and peace of mind.'
    },
    {
        icon: '/images/heart-2-1.svg',
        title: '2-YEAR WARRANTY',
        subtitle: 'A 2-year limited warranty on most residential painting projects.',
        /**
         * Carries the grid placement rule that makes this card span both
         * columns once the strip stacks below 991px.
         */
        nodeId: 'w-node-e0e7d68f-11f5-8549-1955-4c87f08c9de7-83e30275'
    }
]

/** Google reviews of NG Painting Pro, newest first. */
export const testimonials: Testimonial[] = [
    {
        quote: '“Highly recommend. Timely, quality work. Crew arrived on time every day and was courteous, efficient and professional.”',
        name: 'Susan Belle',
        designation: 'April 2026',
        source: 'google'
    },
    {
        quote: '“Norberto and his guys did a fine job. They were prompt, fast, and thorough.”',
        name: 'John Belle',
        designation: 'April 2026',
        source: 'google'
    },
    {
        quote: '“I was a good customer, he painted my house and he is really good and he is responsible and I recommend him to everyone!”',
        name: 'Miguel Reyes',
        designation: 'March 2026',
        source: 'google'
    },
    {
        quote: '“Muy buen servicio. Very good service!”',
        name: 'Jose Bernado Peña Gomez',
        designation: 'March 2026',
        source: 'google'
    },
    {
        quote: '“The best service. Thank you for your great job, I recommend 10/10!!!”',
        name: 'Hermis Rivera',
        designation: 'March 2026',
        source: 'google'
    },
    {
        quote: '“Since day one they have been very understanding and helpful with decisions on areas to paint.”',
        name: 'Sandra Rivera',
        designation: 'July 2024',
        source: 'google'
    },
    {
        quote: '“NG Painting did a fine job. Highly recommend!”',
        name: 'B Marder',
        designation: 'October 2023',
        source: 'google'
    },
    {
        quote: '“Very Professional and Excellent work. Will be recommending to others. Thank you so much!”',
        name: 'Christine',
        designation: 'September 2023',
        source: 'google'
    },
    {
        quote: '“Totally professional and very kind and clean painters.”',
        name: 'Samuel Rodriguez',
        designation: 'June 2023',
        source: 'google'
    }
]

export const processSteps: ProcessStep[] = [
    {
        number: 'STEP 01',
        title: 'Free Estimate',
        subtitle: 'Call 774-301-1382 or send the form. We visit, measure, and give you a clear written quote.'
    },
    {
        number: 'STEP 02',
        title: 'Color & Plan',
        subtitle: 'We help you choose colors and finishes and agree on a schedule that works for you.'
    },
    {
        number: 'STEP 03',
        title: 'Protect & Prep',
        subtitle: 'We protect your belongings, repair surfaces, and prime so the paint bonds properly.'
    },
    {
        number: 'STEP 04',
        title: 'Paint & Walkthrough',
        subtitle: 'We apply premium paint and tidy up daily, then review every area with you and touch up anything before we call the job done.'
    }
]

export const benefits: Benefit[] = [
    {
        icon: '/images/badge-dollar-7653144-1.svg',
        title: 'Transparent Pricing',
        content: 'Free, detailed written estimates with no hidden fees.'
    },
    {
        icon: '/images/hands-heart-1-1.svg',
        title: 'Satisfaction Guaranteed',
        content: 'Satisfaction guaranteed on every project, backed by a 2-year limited warranty on most residential work.'
    },
    {
        icon: '/images/calendar-clock-7602582-1.svg',
        title: 'Flexible Scheduling',
        content: 'Available Monday to Saturday, 7am to 9pm, with commercial work planned around your business hours.'
    }
]

export const differenceCards: DifferenceCard[] = [
    {
        icon: '/images/phone-fast-1-1.svg',
        shadowIcon: '/images/vector-19.svg',
        title: 'Reliability & Trust',
        subtitle: 'We show up on time, every time. Our clients trust us because we keep our promises.'
    },
    {
        icon: '/images/rows-two-alt-1-1.svg',
        shadowIcon: '/images/vector-20.svg',
        title: 'Quality Craftsmanship',
        subtitle: 'We take pride in every brush stroke, with care at every step from surface prep to the final coat.'
    },
    {
        icon: '/images/heart-2-1-1.svg',
        shadowIcon: '/images/vector-21.svg',
        title: 'Customer First',
        subtitle: 'Your satisfaction comes first. We listen, give honest advice, and keep working until you are completely happy.'
    }
]

/**
 * NG Painting Pro does not publish its team, so the About page does not
 * render the team section. Add `{ name, designation, image }` entries
 * here and put `<Team />` back on the About page to show them.
 */
export const teamMembers: TeamMember[] = []

export const faqs: Faq[] = [
    {
        question: 'How much does it cost to paint a house in New Bedford?',
        answer: 'The cost depends on the size of your home, the condition of the surfaces, and the type of paint. Call (774) 301-1382 and we will give you a free quote.'
    },
    {
        question: 'How long does a painting project take?',
        answer: 'Most residential projects take 2 to 5 days, depending on size and complexity. Commercial projects vary based on scope.'
    },
    {
        question: 'Do you offer free estimates?',
        answer: 'Yes! We offer completely free estimates for all painting projects in New Bedford and the surrounding areas.'
    },
    {
        question: 'Are you licensed and insured?',
        answer: 'Yes, NG Painting Pro is fully licensed and insured for your protection and peace of mind.'
    },
    {
        question: 'What are your business hours?',
        answer: 'We are available Monday through Saturday, 7:00 AM to 9:00 PM. We are closed on Sundays.'
    },
    {
        question: 'How long has NG Painting Pro been in business?',
        answer: 'NG Painting Pro has been serving homeowners and businesses in Massachusetts since 2000.'
    },
    {
        question: 'How can I contact NG Painting Pro?',
        answer: 'Call or text (774) 301-1382, email ngpaintingpro1@gmail.com, or request a free estimate through our online form.'
    }
]

export const ctaFeatures = [
    'SERVING MASSACHUSETTS SINCE 2000',
    '500+ HAPPY CUSTOMERS',
    '5.0 STARS ON GOOGLE'
]

export const contactFeatures = [
    {
        title: 'Submit the form',
        subtitle: 'Fill out the form with your project details.'
    },
    {
        title: 'We contact you',
        subtitle: 'Our team reaches out within 24 hours.'
    },
    {
        title: 'Free consultation',
        subtitle: 'We visit your property for an assessment.'
    },
    {
        title: 'Receive your estimate',
        subtitle: 'Get a detailed, no-obligation quote.'
    }
]

export const projectTypes = [
    'Interior Painting',
    'Exterior Painting',
    'Residential Painting',
    'Commercial Painting',
    'Industrial Painting',
    'Pressure Washing',
    'Epoxy Floor Coatings',
    'Stucco Painting and Repair',
    'Light Carpentry',
    'Deck Staining',
    'Wallpaper Removal',
    'Drywall Repair',
    'Multiple Services'
]
